import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { NotificationProvider } from './context/NotificationContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider as CustomThemeProvider, useTheme } from './context/ThemeContext';
import Sidebar from './components/Navigation/Sidebar';
import RepositoryDetails from 'pages/RepositoryDetails';
import Login from './pages/Login';
import { getAuthHeaders } from './services/api';
import { TooltipProvider } from './components/ThreatModelling/ui/tooltip';
import { Toaster } from './components/ThreatModelling/ui/toaster';
import { ProjectProvider } from './context/ProjectContext';
import { GitHubProvider } from './context/GitHubContext';
import { BitbucketProvider } from './context/BitbucketContext';
import ProjectOverview from './components/ProjectOverview';
import NotificationService from './components/common/NotificationService';

import './components/ThreatModelling/ui/toast.css';
import { CheckmarxProvider } from 'context/CheckmarxContext';
import { WizProvider } from 'context/WizContext';
import { JiraProvider } from './context/JiraContext';
import { WalkthroughProvider } from './context/WalkthroughContext';
import GuidedWalkthrough from './components/common/GuidedWalkthrough';
import SecurityAccessCheck from 'components/auth/SecurityAccessCheck';
import { UserProvider } from 'context/UserContext';
import { LoadingProvider, useLoading } from './context/LoadingContext';
import AuthenticatedComponent from 'components/auth/AuthenticatedComponent';
import AuthInitializer from './components/auth/AuthInitializer';
import HorizonViewDetails from 'pages/HorizonViewDetails';
// Lazy loaded components
const Register = React.lazy(() => import('./pages/Register'));
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const EmailVerification = React.lazy(() => import('./pages/EmailVerification'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const PasswordReset = React.lazy(() => import('./pages/PasswordReset'));
const VerificationWaiting = React.lazy(() => import('./pages/VerificationWaiting'));
const AccessDenied = React.lazy(() => import('./pages/AccessDenied'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Projects = React.lazy(() => import('./pages/Projects'));
const ThreatModeling = React.lazy(() => import('./pages/ThreatModeling'));
const ThreatModelScanDetails = React.lazy(() => import('./pages/ThreatModelScanDetails'));
const SBOM = React.lazy(() => import('./pages/SBOM'));
const SCA = React.lazy(() => import('./pages/SCA'));
const SBOMScanDetails = React.lazy(() => import('./pages/SBOMScanDetails'));
const SCAScanDetails = React.lazy(() => import('./pages/SCAScanDetails'));
const SecretScanDetails = React.lazy(() => import('./pages/SecretScanDetails'));
const SecretsDetection = React.lazy(() => import('./pages/SecretsDetection'));
const SAST = React.lazy(() => import('./pages/SAST'));
const APISecurityPage = React.lazy(() => import('./pages/APISecurityPage'));
const ApiScanDetails = React.lazy(() => import('./pages/ApiScanDetails'));
const DAST = React.lazy(() => import('./pages/DAST'));
const Integration = React.lazy(() => import('./pages/Integration'));
const InfraScan = React.lazy(() => import('./pages/InfraScan'));
const Reports = React.lazy(() => import('./pages/Reports'));
const Settings = React.lazy(() => import('./pages/Settings'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Jira = React.lazy(() => import('./pages/Jira'));
const JiraScanDetails = React.lazy(() => import('./pages/JiraScanDetails'));

const LoadingPlaceholder = () => {
  const { theme } = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        backgroundColor: theme === 'dark' ? 'rgba(17, 17, 17, 0.8)' : 'rgba(255, 255, 255, 0.8)'
      }}
    >
      <CircularProgress />
    </Box>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const MainContent = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { theme } = useTheme();

  useEffect(() => {
    // Initial scroll reset
    window.scrollTo(0, 0);
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }

    // Ensure scroll is reset after DOM updates
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (mainContent) {
        mainContent.scrollTop = 0;
      }
      // Double-check with requestAnimationFrame
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        if (mainContent) {
          mainContent.scrollTop = 0;
        }
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <Box
      id="main-content"
      component="main"
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        backgroundColor: theme === 'dark' ? 'rgba(17, 17, 17, 0.95)' : '#FFFFFF',
        scrollBehavior: 'instant !important',
        WebkitOverflowScrolling: 'touch',
        '&': {
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        },
        p: 3
      }}
    >
      {children}
    </Box>
  );
};

const AuthenticatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize notifications for authenticated users
  return (
    <ProjectProvider>
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        '&': {
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }
      }}>
        <Sidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <MainContent>{children}</MainContent>
        </Box>
        {/* Guided walkthrough only for authenticated users */}
        <GuidedWalkthrough />
        {/* Initialize notification services */}
        <NotificationService />
      </Box>
    </ProjectProvider>
  );
};


const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoading } = useLoading();
  const headers = getAuthHeaders();

  if (isLoading) {
    return <LoadingPlaceholder />;
  }

  if (!headers) {
    return <Navigate to="/login" replace />;
  }

  // Wrap with SecurityAccessCheck to verify security permissions
  return (
    <AuthenticatedComponent>
      <AuthenticatedContent>
        <SecurityAccessCheck>
          {children}
        </SecurityAccessCheck>
      </AuthenticatedContent>
    </AuthenticatedComponent>
  );
};

const ProjectRouteContent = () => {
  const location = useLocation();
  const state = location.state as any;

  if (!state) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <ProjectOverview
      projectId={state.projectId}
      auth_id={state.auth_id}
      projectName={state.projectName}
      repositoryUrl={state.repositoryUrl}
      createdAt={state.createdAt}
      updatedAt={state.updatedAt}
      description={state.description}
      tags={state.tags}
      lastScanDate={state.lastScanDate}
    />
  );
};

// Component to handle global services that should persist across navigation
const GlobalServices = () => {
  // This component doesn't render anything but can be used for other global services
  return null;
};


const RouterContent = () => {
  const { theme } = useTheme();

  useEffect(() => {
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
  }, [theme]);

  return (
    <Box sx={{
      display: 'flex',
      width: '100%',
      '&': {
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }
    }}>
      <Suspense fallback={<LoadingPlaceholder />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/verification-waiting" element={<VerificationWaiting />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/project/:id" element={<ProtectedRoute><ProjectRouteContent /></ProtectedRoute>} />
          <Route path="/threat-modeling" element={<ProtectedRoute><ThreatModeling /></ProtectedRoute>} />
          <Route path="/threat-modeling/scan/:scanId" element={<ProtectedRoute><ThreatModelScanDetails /></ProtectedRoute>} />
          <Route path="/sbom" element={<ProtectedRoute><SBOM /></ProtectedRoute>} />
          <Route path="/sca" element={<ProtectedRoute><SCA /></ProtectedRoute>} />
          <Route path="/sbom/scan/:scanId" element={<ProtectedRoute><SBOMScanDetails /></ProtectedRoute>} />
          <Route path="/sca/scan/:scanId" element={<ProtectedRoute><SCAScanDetails /></ProtectedRoute>} />
          <Route path="/secrets" element={<ProtectedRoute><SecretsDetection /></ProtectedRoute>} />
          <Route path="/sast/*" element={<ProtectedRoute><SAST /></ProtectedRoute>} />
          <Route path="/secrets/scan/:scanId" element={<ProtectedRoute><SecretScanDetails /></ProtectedRoute>} />
          <Route path="/secrets/:repoId" element={<ProtectedRoute><RepositoryDetails /></ProtectedRoute>} />
          <Route path="/api-security" element={<ProtectedRoute><APISecurityPage /></ProtectedRoute>} />
          <Route path="/api-security/scan/:scanId" element={<ProtectedRoute><ApiScanDetails /></ProtectedRoute>} />
          <Route path="/dast/*" element={<ProtectedRoute><DAST /></ProtectedRoute>} />
          <Route path="/infra/*" element={<ProtectedRoute><InfraScan /></ProtectedRoute>} />
          <Route path="/integration" element={<ProtectedRoute><Integration /></ProtectedRoute>} />
          <Route path="/integration/horizon-details" element={<ProtectedRoute><HorizonViewDetails /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
          {/* <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
          <Route path="/users/:userId" element={<ProtectedRoute><UserOverview /></ProtectedRoute>} /> */}
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/jira" element={<ProtectedRoute><Jira /></ProtectedRoute>} />
          <Route path="/jira/scan/:scanId" element={<ProtectedRoute><JiraScanDetails /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </Box>
  );
};

const AppContent = () => {
  const { theme } = useTheme();

  return (
    <div className={theme}>
      <SidebarProvider>
        <TooltipProvider>
          <BrowserRouter>
            <AuthInitializer>
              {/* GlobalServices component moved inside BrowserRouter context */}
              <GlobalServices />
              <RouterContent />
            </AuthInitializer>
          </BrowserRouter>
          <Toaster />
        </TooltipProvider>
      </SidebarProvider>
    </div>
  );
};

function App() {
  return (
    <CustomThemeProvider>
      <UserProvider>
        <GitHubProvider>
          <BitbucketProvider>
            <CheckmarxProvider>
              <WizProvider>
               <JiraProvider>
                <NotificationProvider>
                  <WalkthroughProvider>
                    <LoadingProvider>
                      <AppContent />
                    </LoadingProvider>
                  </WalkthroughProvider>
                </NotificationProvider>
                </JiraProvider>
              </WizProvider>
            </CheckmarxProvider>
          </BitbucketProvider>
        </GitHubProvider>
      </UserProvider>
    </CustomThemeProvider>
  );
}

export default App;